// Telling two customers apart when their names do not.
//
// Kevin, 2026-08-26, right after we corrected a line that had been on the wrong account since
// onboarding: "this highlights an issue when two subscribers have the same name, which rare, can
// happen."
//
// IT IS NOT RARE ENOUGH TO IGNORE, and Lyons already has a pair: `John King` at 618 Apple Valley Rd
// and `John "JJ" King` at 113 Eagle Valley Dr. A number that should have been JJ's spent a month on
// his father's account, and the tell was sitting in a support ticket the whole time — ticket #20 said
// "113 Eagle Valley Rd", which is one of those two addresses and not the other.
//
// THE FIX IS NOT TO FORBID DUPLICATE NAMES. Real people share names, and a uniqueness constraint on
// a human name is a bug waiting to be filed by the second Robert Smith in town. The fix is to never
// ask an operator to choose between two rows that read identically — so anywhere a subscriber is
// picked, something that actually differs goes on the row beside the name.
//
// THE SERVICE ADDRESS IS THE DISTINCTION. Kevin, 2026-08-26: "service address should be the
// distinction." So it leads, and the number is the second fact rather than the first.
//
// It is also the better choice on the evidence. Of 96 subscribers, 91 have a service address and 81
// have a number, so it covers more of them — and it is the fact that survives everything else
// changing: people port numbers in and out, take a line off an account, change a mobile. The house
// stays the house. It was also the tell that was already in front of us: ticket #20 said "113 Eagle
// Valley Rd", which is one of the two Kings' addresses and not the other.
//
// NOT FROM `subscribers.service_address`, which is the field whose name promises this and is populated
// on 0 of 96 rows. The real service address lives in `locations`, reached through
// `location_subscribers` so a SHARED address still counts — see store/subscribers.js.
//
// AND NOT A UNIQUE KEY, which is why the number stays on the row behind it. 350 Main St is both Lyons
// Community Church and Lyons Valley Preschool; five subscribers have no address at all. An identifier
// that is usually decisive plus a second fact beats one that claims to be decisive and is not.

/**
 * A name reduced to the part that collides.
 *
 * Nicknames in quotes and generational suffixes are exactly what people ADD to disambiguate
 * themselves — "John 'JJ' King" is somebody's attempt to solve this problem in the name field — so
 * stripping them is how we detect that two rows are about to be confused, not a claim they are the
 * same person. Sorting the words makes "King, John" and "John King" collide too, which they should.
 */
export function normalizeSubscriberName(name) {
  return String(name ?? '')
    .toLowerCase()
    .replace(/"[^"]*"|“[^”]*”|'[^']*'|\([^)]*\)/g, ' ')       // nicknames and parentheticals
    .replace(/\b(jr|sr|ii|iii|iv|jj|dba|and|&)\b/g, ' ')       // suffixes and joiners
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ');
}

/**
 * The other subscribers in `all` whose name would be confused with `sub`'s.
 *
 * Returns [] when the name stands alone — which is the common case, and is why callers should show
 * the extra warning conditionally rather than shouting on every pick.
 */
export function nameTwins(sub, all = []) {
  if (!sub) return [];
  const key = normalizeSubscriberName(sub.name ?? sub.displayName);
  if (!key) return [];
  return all.filter((o) => Number(o.id) !== Number(sub.id)
    && normalizeSubscriberName(o.name ?? o.displayName) === key);
}

/**
 * One short line of facts that distinguish this subscriber, most recognisable first.
 *
 * Falls all the way through to `#id`, which is ugly and always works. A row with nothing on it at all
 * is the one case where an operator genuinely cannot tell — better to show the id than a blank space
 * that reads as "no other information exists".
 */
export function subscriberIdentity(sub, { max = 2 } = {}) {
  if (!sub) return '';
  const facts = [];
  const numbers = sub.numbers || [];
  const addresses = sub.addresses || [];
  if (addresses.length) {
    // ONE address plus a count, never a run-on list. Two subscribers have more than one site, and a
    // row reading "1250 CR37E, 251 Broadway" is harder to match against a ticket than either alone.
    facts.push(addresses.length > 1
      ? `${addresses[0]} +${addresses.length - 1} more`
      : addresses[0]);
  }
  if (numbers.length) facts.push(numbers.map(formatE164).join(', '));
  // `phone` is the contact number on the account rather than a line they own — worth showing when
  // they own no line yet, which is exactly the state a brand-new account is in.
  if (facts.length < max && sub.phone) facts.push(sub.phone);
  if (facts.length < max && sub.email) facts.push(sub.email);
  if (!facts.length) facts.push(`#${sub.id}`);
  return facts.slice(0, max).join(' · ');
}

/** Name plus whatever tells it apart — for a confirmation sentence that has to be unambiguous. */
export function subscriberLabel(sub) {
  if (!sub) return '';
  const name = sub.name ?? sub.displayName ?? `#${sub.id}`;
  const id = subscriberIdentity(sub, { max: 1 });
  return id ? `${name} · ${id}` : name;
}

/** +13039938416 → (303) 993-8416. Left alone if it is not a 10-digit NANP number. */
export function formatE164(e164) {
  const m = String(e164 ?? '').match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : String(e164 ?? '');
}
